import Vue from "vue";
import apiSkobo from "@/helpers/api/apiSkobo";
import ControlledArea from "@/models/Documents/ControlledArea";
import ControlledAreaDefinition from "@/models/Documents/ControlledAreaDefinition";
import DriveItem from "@/models/Documents/DriveItem";

export const controlledArea_state = {
  controlledAreaDefinitionMap: null,
  controlledAreaMap: {},
  combiDriveFolderMap: {}
};

export const controlledArea_actions = {
  async loadControlledAreaDefinitions() {
    try {
      if (!this.controlledAreaDefinitionMap) {
        const response = await apiSkobo.get("/appconfidentialfolder/controlledareas");

        const controlledAreaDefinitionMap = { combiDrives: {} };

        Object.keys(response.data.combiDrives).forEach(system => {
          controlledAreaDefinitionMap["combiDrives"][system.toLowerCase()] = {};

          Object.keys(response.data.combiDrives[system]).forEach(group => {
            controlledAreaDefinitionMap["combiDrives"][system.toLowerCase()][group.toLowerCase()] =
              response.data.combiDrives[system][group].map(df => new ControlledAreaDefinition(df));
          });
        });

        Vue.set(this, "controlledAreaDefinitionMap", controlledAreaDefinitionMap);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  },

  async loadControlledArea(system, entity, group) {
    if (!this.controlledAreaMap[key(system, entity, group)]) {
      await this.reloadControlledArea(system, entity, group);
    }
    return this.controlledAreaMap[key(system, entity, group)];
  },

  async reloadControlledArea(system, entity, group) {
    try {
      const response = await apiSkobo.get(`/appconfidentialfolder/combidrive/${system}/${entity}/${group}`);

      if (response.data) {
        Vue.set(
          this.controlledAreaMap,
          key(system, entity, group),
          response.data.map(area => new ControlledArea(area))
        );
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  },

  async loadCombiDriveFolder(system, entity, group, itemId) {
    itemId = itemId ?? "root";
    if (
      !this.combiDriveFolderMap[key(system, entity, group)] ||
      !this.combiDriveFolderMap[key(system, entity, group)][itemId]
    ) {
      await this.reloadCombiDriveFolder(system, entity, group, itemId);
    }
  },

  async reloadCombiDriveFolder(system, entity, group, itemId) {
    itemId = itemId ?? "root";
    try {
      const subFolders = (await this.loadControlledArea(system, entity, group)).filter(ca => ca.type === "SubFolder");

      if (subFolders.length && subFolders[0].group === "barn") {
        this.reloadConfidentialDocumentsSharedToAulaForUserOnDrive(entity, subFolders[0].driveId);
      }

      await this.loadControlledAreaDefinitions();
      subFolders.forEach(subFolder => {
        if (!subFolder.behavior || typeof subFolder.behavior === "string") {
          const controlledAreaDefinition = this.controlledAreaDefinitionMap["combiDrives"][system.toLowerCase()][
            group.toLowerCase()
          ].find(df => df.identifier === subFolder.identifier);
          subFolder.behavior = controlledAreaDefinition?.behavior;
        }
      });

      let folderDefinitions = [];

      if (itemId === "root") {
        // If itemId is "root" there is no itemId - hence we are at root level. This is the folders defined in controlled area subfolders
        folderDefinitions = await this.reloadCombiDriveRoot2(subFolders);
      } else {
        folderDefinitions = await this.reloadCombiDriveSubFolder(subFolders, itemId);
      }

      if (!this.combiDriveFolderMap[key(system, entity, group)]) {
        Vue.set(this.combiDriveFolderMap, key(system, entity, group), {});
      }

      Vue.set(this.combiDriveFolderMap[key(system, entity, group)], itemId, folderDefinitions);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }

    this.loadCombiDriveFolder(system, entity, group); //Ensure root is loaded
  },

  /**
   * Load combidrive root folders from controlled area
   * @param { Array } subFolders Subfolders of controlled area
   * @returns { Array } Array of driveItems for the subfolders
   */
  async reloadCombiDriveRoot2(subFolders) {
    const promises = subFolders.map(async subFolder => {
      const driveItem = await this.reloadDriveItem(subFolder.driveId, subFolder.itemId, {
        nctType: subFolder.type,
        nctIdentifier: subFolder.identifier,
        nctSystem: subFolder.system,
        nctBehavior: subFolder.behavior,
        nctGroup: subFolder.group
      });

      if (!driveItem) {
        return;
      }

      return {
        driveId: driveItem.parentReference.driveId,
        itemId: driveItem.id,
        name: driveItem.name,
        behavior: subFolder.behavior,
        system: subFolder.system,
        group: subFolder.group,
        identifier: subFolder.identifier
      };
    });
    return await Promise.all(promises);
  },

  /**
   * Load combidrive folder for a specific itemId in the controlled area
   * @param { Array } subFolders Subfolders of controlled area
   * @param { string } itemId ItemId of the folder to load in one of the subfolders
   * @returns { Array } Array of driveItems for the subfolders
   */
  async reloadCombiDriveSubFolder(subFolders, itemId) {
    if (!subFolders.length) {
      return [];
    }

    try {
      const foldersCombiningSelectedFolder = [];
      const selectedDriveItem = await this.reloadDriveItem(subFolders[0].driveId, itemId);

      if (!selectedDriveItem || selectedDriveItem.error) {
        return [];
      }

      foldersCombiningSelectedFolder.push({
        driveId: selectedDriveItem.parentReference.driveId,
        itemId: selectedDriveItem.id,
        name: selectedDriveItem.name,
        behavior: selectedDriveItem.nctBehavior,
        system: subFolders[0].system,
        group: subFolders[0].group,
        identifier: selectedDriveItem.nctIdentifier
      });

      const definitionToStartFrom = subFolders.find(f => f.identifier === selectedDriveItem.nctIdentifier);

      if (definitionToStartFrom) {
        const driveItemToStartFrom = await this.loadDriveItem(
          definitionToStartFrom.driveId,
          definitionToStartFrom.itemId,
          {
            nctType: definitionToStartFrom.type,
            nctIdentifier: definitionToStartFrom.identifier,
            nctSystem: definitionToStartFrom.system,
            nctBehavior: definitionToStartFrom.behavior,
            nctGroup: definitionToStartFrom.group
          }
        );

        const regexStart = "^https:\\/\\/.*\\/sites(\\/[a-zA-Z0-9-%_]*){2}(.*)(\\/";
        const regexEnd = ")(\\/.*)$"; // https://regex101.com/r/N69SBf/1

        const regex = new RegExp(regexStart + encodeURI(driveItemToStartFrom.name) + regexEnd, "g");

        const otherDefitions = subFolders.filter(f => f.identifier !== selectedDriveItem.nctIdentifier);

        if (import.meta.env.VITE_BUILD_TARGET === "kkfiler") {
          const regexResult = regex.exec(encodeURI(selectedDriveItem.webUrl)); // On-prem api delivers webUrl non-encoded
          const postCombiDrivePath = regexResult[4];

          const pathSplit = postCombiDrivePath.split("/");

          const promises = otherDefitions.map(async f => {
            let lastSplitLeg = await this.reloadDriveItem(f.driveId, f.itemId, {
              nctType: f.type,
              nctIdentifier: f.identifier,
              nctSystem: f.system,
              nctBehavior: f.behavior,
              nctGroup: f.group
            });

            if (!lastSplitLeg || lastSplitLeg.error) {
              return null;
            }

            for (let i = 1; i < pathSplit.length; i++) {
              const nextSplitLeg = lastSplitLeg.childrenList.find(c => encodeURI(c.name) === pathSplit[i]);

              if (!nextSplitLeg) {
                lastSplitLeg = null;
                break;
              }

              const currentSplitLeg = await this.reloadDriveItem(
                nextSplitLeg.parentReference.driveId,
                nextSplitLeg.id,
                {
                  nctType: f.type,
                  nctIdentifier: f.identifier,
                  nctSystem: f.system,
                  nctBehavior: f.behavior,
                  nctGroup: f.group
                }
              );

              if (!currentSplitLeg || currentSplitLeg.error) {
                lastSplitLeg = null;
                break;
              }
              lastSplitLeg = currentSplitLeg;
            }
            return lastSplitLeg;
          });

          const children = (await Promise.all(promises)).filter(c => c);
          foldersCombiningSelectedFolder.push(
            ...children.map(c => ({ driveId: c.parentReference.driveId, itemId: c.id, name: c.name }))
          );
        } else {
          const regexResult = regex.exec(selectedDriveItem.webUrl); // Graph api delivers weburl encoded

          const preCombiDrivePath = regexResult[2];
          const postCombiDrivePath = regexResult[4];

          const promises = otherDefitions
            .map(async f => {
              const otherItem = await this.reloadDriveItemFromPath(
                definitionToStartFrom.driveId,
                preCombiDrivePath + "/" + f.identifier + postCombiDrivePath,
                {
                  nctType: f.type,
                  nctIdentifier: f.identifier,
                  nctSystem: f.system,
                  nctBehavior: f.behavior,
                  nctGroup: f.group
                }
              );

              if (otherItem && otherItem.children) {
                return {
                  driveId: otherItem.parentReference.driveId,
                  itemId: otherItem.id,
                  name: otherItem.name,
                  behavior: f.behavior,
                  system: f.system,
                  group: f.group,
                  identifier: f.identifier
                };
              }
            })
            .filter(c => c);

          foldersCombiningSelectedFolder.push(...(await Promise.all(promises)).filter(c => c));
        }
      }

      return foldersCombiningSelectedFolder;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  },

  async calculateCombiDriveFoldersThatAreWritable(system, entity, group, itemId) {
    itemId = itemId ?? "root";
    await this.loadCombiDriveFolder(system, entity, group, itemId);

    const content = this.combiDriveFolderContent(system, entity, group, itemId);

    const promises = content
      .filter(f => f.isFolder)
      .map(async folder => {
        const permission = await this.loadDocumentPermissions(folder.parentReference.driveId, folder.id);
        if (permission && permission.myPermission >= 2) {
          return folder;
        }
        return null;
      });

    return await Promise.all(promises).then(folders => folders.filter(f => f));
  }
};

export const controlledArea_getters = {
  combiDriveFolderContent(state) {
    return (system, entity, group, itemId) => {
      itemId = itemId ?? "root";
      if (
        !state.combiDriveFolderMap[key(system, entity, group)] ||
        !state.combiDriveFolderMap[key(system, entity, group)][itemId]
      ) {
        return [];
      }

      const listOfChildrenFromHigherLevel = [];

      const folders = JSON.parse(JSON.stringify(state.combiDriveFolderMap[key(system, entity, group)][itemId]));
      folders.sort((a, b) => a?.behavior?.level - b?.behavior?.level);

      folders.forEach(folder => {
        this.driveItem(folder.driveId, folder.itemId)
          .childrenList.filter(c => listOfChildrenFromHigherLevel.map(l => l.name).indexOf(c.name) === -1)
          .forEach(c => listOfChildrenFromHigherLevel.push(new DriveItem(c)));
      });

      listOfChildrenFromHigherLevel.forEach(item => {
        if (this.isConfidentialDocumentShareToAulaForUserIdOnDriveId(entity, item.parentReference.driveId, item.id)) {
          item.nctSharedToAula = true;
        } else {
          item.nctSharedToAula = false;
        }

        item.nctJournalizing = this.isDocumentJournalizing(
          this.appConfidentialId,
          item.parentReference.driveId,
          item.id
        );

        item.nctLocking = this.isDocumentLocking(this.appConfidentialId, item.parentReference.driveId, item.id);
        item.nctSpecialItem = this.specialItemObjectForDriveItem(item.parentReference.driveId, item.id);
      });

      return listOfChildrenFromHigherLevel;
    };
  },

  combiDriveRootDefaultFolder(state) {
    return (system, entity, group) => {
      if (
        !state.combiDriveFolderMap[key(system, entity, group)] ||
        !state.combiDriveFolderMap[key(system, entity, group)]["root"]
      ) {
        return;
      }
      const rootControlledAreaDefault = state.combiDriveFolderMap[key(system, entity, group)]["root"].find(
        cdf => cdf.behavior?.level === 0
      );

      if (!rootControlledAreaDefault) {
        return;
      }
      return this.driveItem(rootControlledAreaDefault.driveId, rootControlledAreaDefault.itemId);
    };
  },

  controlledAreaKeysFromDriveIdAndItemId(state) {
    return (driveId, itemId) => {
      let key;

      for (const cdf of Object.keys(state.combiDriveFolderMap)) {
        const items = Object.values(state.combiDriveFolderMap[cdf]) || [];
        for (const cdfItem of items) {
          const cd = cdfItem.find(x => x && x.driveId === driveId && x.itemId === itemId);
          if (cd) {
            return splitKey(cdf);
          }
        }
      }
      return splitKey(key);
    };
  },

  controlledAreaRootOfCombiDriveFromDriveIdAndMetas(state) {
    return (driveId, itemId, system, identifier, group) => {
      let key;

      Object.keys(state.combiDriveFolderMap).every(k => {
        Object.keys(state.combiDriveFolderMap[k]).every(f => {
          Object.keys(state.combiDriveFolderMap[k][f]).every(g => {
            if (
              state.combiDriveFolderMap[k][f][g].driveId === driveId &&
              state.combiDriveFolderMap[k][f][g].itemId === itemId
            ) {
              key = k;
              return false;
            }
            return true;
          });
          if (key) {
            return false;
          }
          return true;
        });
        if (key) {
          return false;
        }
        return true;
      });

      return state.controlledAreaMap[key].find(
        f => f.system === system && f.identifier === identifier && f.group === group
      );
    };
  },

  controlledAreaFromDriveIdAndItemId(state) {
    return (driveId, itemId) => {
      const controlledAreaMapKeys = Object.keys(state.controlledAreaMap);
      for (let i = 0; i < controlledAreaMapKeys.length; i++) {
        const foundControlledArea = state.controlledAreaMap[controlledAreaMapKeys[i]].find(
          caItem => caItem.driveId === driveId && caItem.itemId === itemId
        );

        if (foundControlledArea) {
          return foundControlledArea;
        }
      }
      return undefined;
    };
  },

  rootControlledAreaDefinitionFromDriveIdAndItemId(state) {
    return (driveId, itemId) => {
      let groupKey;
      Object.keys(state.controlledAreaMap).forEach(key => {
        if (state.controlledAreaMap[key].find(caItem => caItem.driveId === driveId && caItem.itemId === itemId)) {
          groupKey = key;
        }
      });

      if (groupKey) {
        return state.controlledAreaMap[groupKey].find(caItem => caItem.type === "RootFolder");
      }

      return null;
    };
  },

  controlledAreaDriveId(state) {
    return (system, entity, group) => {
      if (state.controlledAreaMap[key(system, entity, group)]) {
        return state.controlledAreaMap[key(system, entity, group)][0]?.driveId;
      }
    };
  },

  controlledAreaConfidentialId(state) {
    return (system, entity, group) => {
      if (state.controlledAreaMap[key(system, entity, group)]) {
        return state.controlledAreaMap[key(system, entity, group)].find(ca => ca.type === "RootFolder")?.confidentialId;
      }
    };
  },

  defaultToolbarControlledArea(state) {
    return (system, group, identifier) => {
      if (
        state.controlledAreaDefinitionMap &&
        state.controlledAreaDefinitionMap["combiDrives"] &&
        state.controlledAreaDefinitionMap["combiDrives"][system?.toLowerCase()] &&
        state.controlledAreaDefinitionMap["combiDrives"][system?.toLowerCase()][group?.toLowerCase()]
      ) {
        const definition = state.controlledAreaDefinitionMap["combiDrives"][system.toLowerCase()][
          group.toLowerCase()
        ].find(def => def.identifier === identifier);

        return definition?.toolbarDefault;
      }
      return null;
    };
  },

  controlledAreaGroupsForSystem(state) {
    return system => {
      if (
        state.controlledAreaDefinitionMap &&
        state.controlledAreaDefinitionMap["combiDrives"] &&
        state.controlledAreaDefinitionMap["combiDrives"][system?.toLowerCase()]
      ) {
        const groupKeys = Object.keys(state.controlledAreaDefinitionMap["combiDrives"][system.toLowerCase()]).sort(
          (a, b) => {
            return (
              state.controlledAreaDefinitionMap["combiDrives"][system.toLowerCase()][a][0]?.groupSort -
              state.controlledAreaDefinitionMap["combiDrives"][system.toLowerCase()][b][0]?.groupSort
            );
          }
        );

        const groups = groupKeys.map(groupKey => {
          return {
            name: groupKey,
            color:
              state.controlledAreaDefinitionMap["combiDrives"][system.toLowerCase()][groupKey][0]?.behavior?.tabColor ||
              null
          };
        });
        return groups;
      }
    };
  }
};

/**
 * Generate key for the controlled area map
 * @param { string } system System
 * @param { string } entity Entity
 * @param { string } group  Group
 * @returns { string } Key for the controlled area map
 */
function key(system, entity, group) {
  return `${system?.toLowerCase()}_${entity?.toLowerCase()}_${group?.toLowerCase()}`;
}

/**
 * Split key from controlled area map
 * @param { string } key Key to split
 * @returns { object } Object with system, entity and group
 */
function splitKey(key) {
  if (!key) {
    return null;
  }

  const split = key.split("_");
  return {
    system: split[0],
    entity: split[1],
    group: split[2]
  };
}
